import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Menu,
  MenuItem,
} from '@mui/material';
import { motion } from 'framer-motion';
import { get } from 'lodash';
import { forwardRef, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import useThemeStore from 'Theme/store';
// Mobx
import { inject, observer } from 'mobx-react';
// Icons
import {
  ArrowsRightLeftIcon,
  ChatBubbleLeftRightIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  SpeakerWaveIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  TrophyIcon,
  UserCircleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  AcademicCapIcon,
  BookOpenIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  CommandLineIcon,
  NewspaperIcon,
  PhotoIcon as PhotoIconSolid,
  RectangleStackIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Tooltip } from '@mui/material';
import PencilSquareIcon from '../../../Theme/icons/editIcon';
import ModelsIcon from '../../../Theme/icons/modelsIcon';
import { icons as modelIcons } from '../../../Theme/icons/modelsIcons/index';
import TemplatesIcon from '../../../Theme/icons/templatesIcon';
// Components
import ConversationSide from 'Components/Chat/ConversationSide/ConversationSide';
import PromptSide from 'Components/Chat/PromptSide/PromptSide';
import useChatStore, {
  CAPABILITIES_CHANGE,
  EVENT_NEW_CHAT,
  EVENT_SELECT_CHAT,
  LOADING_FAV_TOOLS,
  SCRIPT_FOR_TTS,
} from 'Components/Chat/store';
import ToggleTheme from 'Components/Common/ToggleTheme';
import Loader from 'Components/Loader';
import toast from 'react-hot-toast';
import FloppyDiskIcon from '../../../Theme/icons/floppyDiskIcon';
import {
  ChooseTemplate,
  CreateSmartTemplate,
  CreateTemplatesForm,
} from 'Features/CreateTemplatesForm';

const MobilePowerPanel = forwardRef(
  ({ store, onClose, setOpenDialogDiscord }, ref) => {
    const { theme: themeValue } = useThemeStore();

    const [openModels, setOpenModels] = useState(false);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openSettings, setOpenSettings] = useState(false);
    const [loadingFavTools, setLoadingFavTools] = useState(false);
    const [favTools, setFavTools] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    const loadingChats = useChatStore((state) => state.loadingChats);
    const selectedChat = useChatStore((state) => state.selectedChat);
    const chatList = useChatStore((state) => state.chatList);
    const selectedModels = useChatStore((state) => state.selectedModels);
    const setShowModelsDialog = useChatStore(
      (state) => state.setShowModelsDialog
    );
    const addEvent = useChatStore((state) => state.addEvent);
    const currentEvent = useChatStore((state) => state.currentEvent);
    const runEvent = useChatStore((state) => state.runEvent);
    const setPowerPanelOpen = useChatStore((state) => state.setPowerPanelOpen);

    const {
      combos,
      combosLoading,
      selectedCombo,
      fetchCombos,
      setSelectedCombo,
      createCombo,
    } = useChatStore();

    const [allModels, setAllModels] = useState([]);

    const [comboName, setComboName] = useState('');

    const [error, setError] = useState('');

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null);

    const maxComboNameLength = 15;

    const invalidName = 'Custom Combination';

    const updateSelectedModels = useChatStore(
      (state) => state.updateSelectedModels
    );

    useEffect(() => {
      const fetchModels = async () => {
        try {
          const response = await store.api.get(`/model`);
          setAllModels(response.data.models || []);
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      };

      fetchModels();
    }, [store.api]);

    // Capabilities variables
    const [capabilities, setCapabilities] = useState(
      JSON.parse(localStorage.getItem('capabilities')) || []
    );

    const baseAccordionStyles = () => {
      return {
        backgroundColor: themeValue === 'dark' ? '#1D1C1E' : '#EDEDF2',
        color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
        '&:before': {
          display: 'none',
        },
        overflow: 'hidden',
      };
    };

    const handleComboClick = async (combo) => {
      setSelectedCombo(combo);

      if (combo && Array.isArray(combo.llms)) {
        try {
          const selectedModels = combo.llms
            .map((llmId) => allModels.find((m) => m._id === llmId))
            .filter(Boolean)
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

          updateSelectedModels(() => selectedModels);
          localStorage.setItem('currentModels', JSON.stringify(selectedModels));
        } catch (error) {
          console.error('Error processing combo:', error);
        }
      }
      handleMenuClose();
    };

    const handleSaveClick = () => {
      setComboName('');
      setError('');
      setIsDialogOpen(true);
    };
    const handleDialogClose = () => {
      setIsDialogOpen(false);
      setComboName('');
      setError('');
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const validateComboName = (name) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        return 'The combo name cannot be empty.';
      }
      if (trimmedName.toLowerCase() === invalidName.toLowerCase()) {
        return `The name "${invalidName}" is not allowed.`;
      }
      if (trimmedName.length > maxComboNameLength) {
        return `The name cannot exceed ${maxComboNameLength} characters.`;
      }
      const isDuplicateName = combos.some(
        (combo) => combo.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (isDuplicateName) {
        return 'A combo with this name already exists.';
      }
      return null;
    };

    const handleSaveCombo = async () => {
      const trimmedName = comboName.trim();
      const validationError = validateComboName(trimmedName);

      if (validationError) {
        setError(validationError);
        toast.error(validationError);
        return;
      }

      // Ensure no combo with the exact same set of models
      const selectedModelIds = selectedCombo.llms.slice().sort();
      const isDuplicateModels = combos.some((combo) => {
        const comboModelIds = combo.llms.slice().sort();
        return (
          comboModelIds.length === selectedModelIds.length &&
          comboModelIds.every((id, idx) => id === selectedModelIds[idx])
        );
      });
      if (isDuplicateModels) {
        const msg = 'A combo with the same models already exists.';
        setError(msg);
        toast.error(msg);
        return;
      }

      // Attempt to create
      try {
        const comboData = { ...selectedCombo, name: trimmedName };
        const newCombo = await createCombo(comboData);

        await fetchCombos();
        setSelectedCombo(newCombo);
        toast.success('Combo saved successfully!');
        handleDialogClose();
      } catch (error) {
        console.error('Error saving combo:', error);
        toast.error('Failed to save combo.');
      }
    };

    const accordionStyles = () => {
      return {
        ...baseAccordionStyles(),
        '&.Mui-expanded': {
          flex: '1 1 0%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
        '& > .MuiCollapse-root': {
          flex: '1 1 0%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 266px)',
        },
        '& > .MuiCollapse-root > .MuiCollapse-wrapper': {
          flex: '1 1 0%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
        '& > .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner':
          {
            flex: '1 1 0%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          },
        '& > .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > .MuiAccordion-region':
          {
            flex: '1 1 0%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          },
        '& > .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > .MuiAccordion-region > .MuiAccordionDetails-root':
          {
            flex: '1 1 0%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          },
      };
    };

    const getIcon = (icon) => {
      const Icon = get(modelIcons, icon);
      return Icon ? (
        <Icon className="h-6 w-6" />
      ) : (
        <ModelsIcon className="h-6 w-6 stroke-raisin-black dark:stroke-crystal-bell" />
      );
    };

    const handleAccordionChange = (accordionId) => (event, isExpanded) => {
      navigate('/chat');
      setOpenSettings(false);
      setOpenAccordion(isExpanded ? accordionId : false);
    };

    const getImageProfile = () => store.profile.avatar.svg;

    const handleOpenSettings = () => {
      setOpenModels(false);
      setOpenAccordion(null);
      setOpenSettings(!openSettings);
    };

    const sortArrayAlphabetically = (array) => {
      return array.sort((a, b) => a.title.localeCompare(b.title));
    };

    const fetchFavs = async () => {
      store.cachedFavoritesResponse = null;
      setLoadingFavTools(true);
      try {
        const data = await store.fetchFavorites();
        if (data && data.data) {
          const filteredTools = data.data.filter((tool) => !tool.in_trash);
          setFavTools(sortArrayAlphabetically(filteredTools));
          store.setFavTools(sortArrayAlphabetically(filteredTools));
        } else {
          console.error('No favorite tools or unexpected format found:', data);
        }
      } catch (error) {
        console.error('Error getting favorite tools:', error);
      } finally {
        setLoadingFavTools(false);
      }
    };

    const onClickTemplate = (tool) => {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('selectedTool', tool?._id);
      navigate(
        {
          pathname: location.pathname !== '/chat' ? '/chat' : location.pathname,
          search: urlParams.toString(),
        },
        { shallow: true }
      );
    };

    useEffect(() => {
      fetchFavs();
    }, []);

    useEffect(() => {
      if (currentEvent) {
        eventActions(currentEvent);
      }
    }, [currentEvent]);

    const eventActions = (event) => {
      switch (event.name) {
        case LOADING_FAV_TOOLS:
          runEvent(fetchFavs);
          break;
        case CAPABILITIES_CHANGE:
          runEvent(refreshCapabilities);
          break;
        default:
          break;
      }
    };

    const refreshCapabilities = () => {
      setCapabilities(JSON.parse(localStorage.getItem('capabilities')) || []);
    };

    const checkCapabilities = (model) => {
      if (model.function_calls && model.function_calls.length) {
        // find capability
        let modelCapabilities = capabilities.filter((capability) =>
          model.function_calls
            .map((func) => func.capability)
            .includes(capability._id)
        );
        return modelCapabilities;
      }
      return null;
    };

    const activeCapability = (model) => {
      const cap = checkCapabilities(model);

      return cap && cap.length;
    };

    const getToolIconByCategory = (tool) => {
      switch (tool.category) {
        case 'Image Generation':
          return <PhotoIconSolid className="w-4 h-4" />;
        case 'Writing':
          return <ClipboardDocumentListIcon className="w-4 h-4" />;
        case 'Resources':
          return <RectangleStackIcon className="w-4 h-4" />;
        case 'Business':
          return <BriefcaseIcon className="w-4 h-4" />;
        case 'Career Development':
          return <AcademicCapIcon className="w-4 h-4" />;
        case 'Content':
          return <NewspaperIcon className="w-4 h-4" />;
        case 'Programming':
          return <CommandLineIcon className="w-4 h-4" />;
        case 'Image Prompting':
          return <SparklesIcon className="w-4 h-4" />;
        default:
          return <BookOpenIcon className="w-4 h-4" />;
      }
    };

    const handleDialogCloseWrapper = (event, reason) => {
      if (reason === 'backdropClick') {
        return;
      }
      handleDialogClose();
    };

    const handleNavigation = (path) => {
      navigate(path);
      setTimeout(() => {
        const targetSection = document.querySelector('#wordcapLimitsSection');
        if (targetSection) {
          targetSection.scrollIntoView({ behavior: 'smooth' });
          targetSection.focus();
        }
      }, 500);
    };

    const handleMenuOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
      if (!combosLoading && combos.length === 0) {
        fetchCombos();
      }
    }, [combosLoading, combos, fetchCombos]);

    return (
      <>
        <motion.div
          initial={{ x: -320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -320, opacity: 0 }}
          ref={ref}
          className="absolute top-0 left-0 h-full z-50 pt-5 flex flex-col items-center bg-ghost-white dark:bg-umbra duration-2000 w-80 font-figtree"
        >
          {/* Templates */}
          <ChooseTemplate />
          <CreateTemplatesForm />
          <CreateSmartTemplate store={store} />
          <div className="w-full mb-4 flex items-center justify-between px-5">
            <div onClick={handleOpenSettings}>
              {store.profile.avatar.svg ? (
                <div className="avatar">
                  <div className="w-12 rounded">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getImageProfile(),
                      }}
                    />
                  </div>
                </div>
              ) : (
                <UserCircleIcon
                  className={`w-12 h-12 transition ${
                    themeValue === 'dark'
                      ? 'dark:text-palladium'
                      : 'text-raisin-black'
                  }`}
                />
              )}
            </div>
            <Tooltip
              title="Open text to speech generator"
              arrow
              placement="bottom"
            >
              <div
                className="w-8 h-8 flex justify-center items-center rounded-xl cursor-pointer select-none"
                onClick={() => addEvent(SCRIPT_FOR_TTS)}
              >
                <SpeakerWaveIcon className="w-[30px] h-[30px] text-nue-blue" />{' '}
              </div>
            </Tooltip>
            <Tooltip
              title="Access Stable Diffusion image generator"
              arrow
              placement="bottom"
            >
              <div
                className="w-8 h-8 flex justify-center items-center rounded-xl cursor-pointer select-none"
                onClick={() => {
                  navigate('/images');
                  onClose();
                }}
              >
                <PhotoIcon className="w-[46px] h-[46px] text-nue-blue" />
              </div>
            </Tooltip>
            <div
              className="flex items-center justify-center gap-2 px-2 h-[38px] text-white bg-nue-blue hover:bg-nue-blue/70 cursor-pointer rounded-xl select-none shadow-md"
              onClick={() => {
                addEvent(EVENT_NEW_CHAT);
                onClose();
              }}
            >
              <PlusCircleIcon className="h-[21px] w-[21px]" />
              <span>New chat</span>
            </div>
          </div>

          {openSettings && (
            <motion.div
              initial={{ y: -242, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -242, opacity: 0 }}
              className={`${
                themeValue === 'dark' ? 'bg-umbra' : 'bg-ghost-white'
              } w-full px-5`}
            >
              <div className="flex justify-around">
                <button
                  onClick={() => {
                    setOpenDialogDiscord(true);
                    onClose();
                  }}
                  className={`flex items-center justify-center px-1 rounded-md text-sm ${
                    themeValue === 'dark'
                      ? 'hover:bg-ship-grey text-crystal-bell'
                      : 'hover:bg-lavender text-cool-gray'
                  }`}
                >
                  <img
                    className="h-6 px-1 block"
                    src={
                      themeValue === 'dark'
                        ? '/svg/discord-mark-white.svg'
                        : '/svg/discord-mark-blue.svg'
                    }
                    alt="Discord"
                  />
                </button>
                <ToggleTheme />
                <a
                  href="https://straico.com/multimodel/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex "
                  onClick={onClose}
                >
                  <button
                    className={`flex items-center justify-center px-1 rounded-md ${
                      themeValue === 'dark'
                        ? 'text-crystal-bell hover:bg-ship-grey'
                        : 'text-cool-gray hover:bg-ship-grey'
                    }  text-sm`}
                  >
                    <QuestionMarkCircleIcon className="w-7 h-7 mx-1" />
                  </button>
                </a>
              </div>
              <div className="bg-cool-gray h-[3px] w-auto mx-8 my-3 rounded-full"></div>
              <div className="text-cool-gray text-sm leading-6">
                <NavLink
                  to="/my-profile"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `px-3 py-2 flex rounded-lg cursor-pointer items-center ${
                      isActive
                        ? 'bg-nue-blue hover:bg-nue-blue/70 text-white transition'
                        : `${
                            themeValue === 'dark'
                              ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
                              : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
                          }`
                    }`
                  }
                  end
                >
                  <li className="flex items-center justify-center">
                    {store.profile.avatar.svg ? (
                      <div className="avatar">
                        <div className="w-6 rounded">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: store.profile.avatar.svg,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <UserCircleIcon
                        className={`w-5 h-5 transition`}
                        to="/my-profile"
                      />
                    )}
                    <span className="origin-left duration-200 ml-2">
                      My profile
                    </span>
                  </li>
                </NavLink>

                <NavLink
                  to="/transactions"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `px-3 py-2 flex rounded-lg cursor-pointer items-center ${
                      isActive
                        ? 'bg-nue-blue hover:bg-nue-blue/70 text-white transition'
                        : `${
                            themeValue === 'dark'
                              ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
                              : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
                          }`
                    }`
                  }
                >
                  <li className="flex items-center justify-center">
                    <ArrowsRightLeftIcon
                      className={`w-5 h-5 transition`}
                      to="/transactions"
                    />
                    <span className="origin-left duration-200 ml-2">
                      Transactions
                    </span>
                  </li>
                </NavLink>

                <NavLink
                  to="/my-profile/pricing"
                  onClick={onClose}
                  end
                  className={({ isActive }) =>
                    `px-3 py-2 flex rounded-lg cursor-pointer items-center ${
                      isActive
                        ? 'bg-nue-blue hover:bg-nue-blue/70 text-white transition'
                        : `${
                            themeValue === 'dark'
                              ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
                              : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
                          }`
                    }`
                  }
                >
                  <li className="flex items-center justify-center">
                    <CreditCardIcon
                      to="/my-profile/pricing"
                      end
                      className={`w-5 h-5 transition`}
                    />
                    <span className="origin-left duration-200 ml-2">
                      Billing and plans
                    </span>
                  </li>
                </NavLink>

                <NavLink
                  to="/rewards"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `px-3 py-2 flex rounded-lg cursor-pointer items-center ${
                      isActive
                        ? 'bg-nue-blue hover:bg-nue-blue/70 text-white transition'
                        : `${
                            themeValue === 'dark'
                              ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
                              : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
                          }`
                    }`
                  }
                >
                  <li className="flex items-center justify-center">
                    <TrophyIcon
                      className={`w-5 h-5 transition`}
                      to="/rewards"
                    />
                    <span className="origin-left duration-200 ml-2">
                      Rewards
                    </span>
                  </li>
                </NavLink>

                <div
                  className={`px-3 py-2 flex rounded-lg cursor-pointer items-center ${
                    themeValue === 'dark'
                      ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
                      : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
                  }`}
                >
                  <a
                    href="https://straico.com/desktop-app/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <li className="flex items-center justify-center">
                      <ComputerDesktopIcon className={`w-5 h-5 transition`} />
                      <span className="origin-left duration-200 ml-2">
                        Desktop app <span className="text-[10px]">Beta</span>
                      </span>
                    </li>
                  </a>
                </div>
              </div>
              <div className="bg-cool-gray h-[3px] w-auto mx-8 my-3 rounded-full"></div>
            </motion.div>
          )}

          <div className="w-full h-full flex-1 flex flex-col overflow-auto">
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={baseAccordionStyles()}
              expanded={openModels}
              onChange={(event, isExpanded) => {
                setOpenModels(isExpanded);
                setOpenSettings(false);
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
                    }}
                  />
                }
                aria-controls="models-content"
                id="models-header"
              >
                <ModelsIcon className="h-[21px] w-[21px] stroke-raisin-black dark:stroke-crystal-bell mr-3" />
                <div className="flex-1 font-semibold">Current models</div>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <div
                  className={`flex gap-2 items-center rounded-xl p-2 border-[1.5px] ${
                    themeValue === 'dark'
                      ? 'bg-lead border-sonic-silver'
                      : 'bg-white border-cool-gray/35'
                  }`}
                >
                  <div className="flex-1 flex flex-col gap-2">
                    {selectedModels.map((model, index) => (
                      <Badge
                        color="primary"
                        variant="dot"
                        invisible={!activeCapability(model)}
                        key={index}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                      >
                        <div key={index} className="flex gap-2 items-center">
                          {getIcon(model.icon)}
                          <span className="flex-1 line-clamp-1 text-sm text-agate-violet dark:text-palladium">
                            {model.abbreviation}
                          </span>
                        </div>
                      </Badge>
                    ))}
                  </div>
                  <Tooltip title="Choose models" arrow placement="right">
                    <div
                      onClick={() => {
                        navigate('/chat');
                        onClose();
                        setShowModelsDialog(true);
                      }}
                      className={`w-[32px] h-[32px] bg-lavender hover:bg-ghost-white cursor-pointer rounded-[10px] dark:bg-ship-grey dark:hover:bg-quicksilver justify-center items-center flex select-none`}
                    >
                      <PencilSquareIcon className="w-[5] h-[5] text-violet-blue dark:text-tropical-indigo" />
                    </div>
                  </Tooltip>
                </div>
                <div className="flex items-center mt-4">
                  {/* Combos select / "custom" */}
                  <div className="flex-grow flex-shrink w-full">
                    {combosLoading && <p>Loading combos...</p>}
                    {!combosLoading && combos.length === 0 && (
                      <p>No combos available</p>
                    )}
                    {!combosLoading && combos.length > 0 && (
                      <div
                        onClick={handleMenuOpen}
                        style={{
                          backgroundColor:
                            themeValue === 'dark' ? '#252425' : '#FBFBFA',
                          border:
                            themeValue === 'dark'
                              ? '1.5px solid #5A5A62'
                              : '1.5px solid #9091A461',
                          borderRadius: '10px',
                          padding: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            marginLeft: '8px',
                            fontSize: '14px',
                            color:
                              themeValue === 'dark' ? '#ffffff' : '#211E21',
                          }}
                        >
                          {selectedCombo?.name || 'No combo selected'}
                        </p>
                        <ChevronDownIcon
                          className={`h-5 w-5 text-violet-blue dark:text-tropical-indigo cursor-pointer transition-transform duration-200 ${
                            Boolean(anchorEl) ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                    )}
                  </div>
                  {selectedCombo?._id === 'custom' && (
                    <Tooltip title="Save New Combo" arrow placement="right">
                      <div
                        onClick={handleSaveClick}
                        className={`w-[32px] h-[32px] bg-lavender hover:bg-ghost-white cursor-pointer rounded-[10px] dark:bg-ship-grey dark:hover:bg-quicksilver justify-center items-center flex select-none`}
                      >
                        <FloppyDiskIcon className="w-[5] h-[5] text-violet-blue dark:text-tropical-indigo" />
                      </div>
                    </Tooltip>
                  )}
                  <div
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onTouchMove={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Menu
                      anchorEl={anchorEl}
                      MenuListProps={{
                        onClick: (e) => e.stopPropagation(),
                        onMouseDown: (e) => e.stopPropagation(),
                        onTouchStart: (e) => e.stopPropagation(),
                      }}
                      PopoverProps={{
                        disableAutoFocus: true,
                        disableEnforceFocus: true,
                        disableRestoreFocus: true,
                        onClick: (e) => e.stopPropagation(),
                        onTouchStart: (e) => e.stopPropagation(),
                      }}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        style: {
                          maxHeight: '400px',
                          overflow: 'hidden',
                          width: anchorEl ? anchorEl.clientWidth : 'auto',
                          backgroundColor:
                            themeValue === 'dark' ? '#252425' : '#FBFBFA',
                          border: '1.5px solid #9091A461',
                          borderRadius: '10px',
                          marginTop: '6px',
                          boxShadow: 'none',
                        },
                        elevation: 0,
                      }}
                    >
                      {combosLoading && (
                        <MenuItem disabled>Loading combos...</MenuItem>
                      )}
                      {!combosLoading && combos.length === 0 && (
                        <MenuItem disabled>No combos available</MenuItem>
                      )}
                      {!combosLoading && combos.length > 0 && (
                        <>
                          <div
                            style={{
                              maxHeight: '200px',
                              overflowY: 'auto',
                              backgroundColor:
                                themeValue === 'dark' ? '#252425' : '#FBFBFA',
                            }}
                          >
                            {combos.map((combo) => (
                              <MenuItem
                                key={combo._id}
                                onClickCapture={(e) => {
                                  e.stopPropagation();
                                  handleComboClick(combo);
                                }}
                                style={{
                                  backgroundColor:
                                    themeValue === 'dark'
                                      ? '#252425'
                                      : '#FBFBFA',
                                  color:
                                    themeValue === 'dark'
                                      ? '#FFFFFF'
                                      : '#211E21',
                                  borderRadius: '6px',
                                  margin: '4px 0',
                                  padding: '8px 16px',
                                  transition: 'background-color 0.3s ease',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor =
                                    themeValue === 'dark'
                                      ? '#3B3B45'
                                      : '#EDEDF2';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor =
                                    themeValue === 'dark'
                                      ? '#252425'
                                      : '#FBFBFA';
                                }}
                              >
                                {combo.name}
                              </MenuItem>
                            ))}
                          </div>
                          <div
                            style={{
                              textAlign: 'center',
                              backgroundColor:
                                themeValue === 'dark' ? '#252425' : '#FBFBFA',
                            }}
                          >
                            <Button
                              variant="contained"
                              onClickCapture={(e) => {
                                e.stopPropagation();
                                handleNavigation(
                                  '/user-settings#personalizeAI'
                                );
                                onClose();
                                setAnchorEl(null);
                              }}
                              startIcon={
                                <ArrowTopRightOnSquareIcon
                                  style={{
                                    width: 20,
                                    height: 20,
                                    color:
                                      themeValue === 'dark'
                                        ? '#7F82C3'
                                        : '#5256A6',
                                  }}
                                />
                              }
                              style={{
                                backgroundColor:
                                  themeValue === 'dark' ? '#3B3B45' : '#D4D5E6',
                                color:
                                  themeValue === 'dark' ? '#7F82C3' : '#5256A6',
                                textTransform: 'none',
                                width: '90%',
                                height: 'auto',
                                boxShadow: 'none',
                                fontSize: '13px',
                                whiteSpace: 'normal',
                              }}
                              PaperProps={{
                                style: {
                                  boxShadow: 'none',
                                },
                                elevation: 0,
                              }}
                            >
                              Go to settings page
                            </Button>
                          </div>
                        </>
                      )}
                    </Menu>{' '}
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={accordionStyles()}
              expanded={openAccordion === 'chats'}
              onChange={handleAccordionChange('chats')}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
                    }}
                  />
                }
                aria-controls="chats-content"
                id="chats-header"
              >
                <ChatBubbleLeftRightIcon className="h-[21px] w-[21px] mr-3" />
                <div className="flex-1 font-semibold">My chats</div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <motion.div className="z-10 h-full relative flex-1 flex flex-col overflow-auto">
                  <ConversationSide
                    show={true}
                    onClick={() => {
                      addEvent(EVENT_NEW_CHAT);
                      onClose();
                    }}
                    onSelectChat={(chat) => {
                      addEvent(EVENT_SELECT_CHAT, chat);
                      onClose();
                    }}
                    loading={loadingChats}
                    chatSelectedIndex={selectedChat?.index}
                    chats={chatList}
                    currentChatId={selectedChat?.chat}
                    newDesing
                  />
                </motion.div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={accordionStyles()}
              expanded={openAccordion === 'templates'}
              onChange={handleAccordionChange('templates')}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
                    }}
                  />
                }
                aria-controls="tamplates-content"
                id="tamplates-header"
              >
                <TemplatesIcon className="h-[21px] w-[21px] stroke-raisin-black dark:stroke-crystal-bell mr-3" />
                <div className="flex-1 font-semibold">Templates</div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <PromptSide newDesing />
              </AccordionDetails>
            </Accordion>
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={accordionStyles()}
              expanded={openAccordion === 'favorites'}
              onChange={handleAccordionChange('favorites')}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: '0.9rem',
                      color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
                    }}
                  />
                }
                aria-controls="favotites-content"
                id="favotites-header"
              >
                <StarIcon className="h-[21px] w-[21px] mr-3" />
                <div className="flex-1 font-semibold">Favorite Templates</div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <div className="flex-1 h-full flex flex-col font-figtree">
                  {loadingFavTools ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader />
                    </div>
                  ) : favTools.length > 0 ? (
                    <>
                      <div
                        className={`flex items-center relative rounded-[10px] transition mx-5 mb-2 ${
                          themeValue === 'dark'
                            ? 'focus-within:border-[#787878]'
                            : 'focus-within:border-[#CFD0D6]'
                        } border-2 border-transparent rounded-[12px]`}
                      >
                        <input
                          autoFocus
                          id="keyword"
                          className={`appearance-none ${
                            themeValue === 'dark'
                              ? 'dark:bg-night-black dark:text-crystal-bell'
                              : 'bg-white text-raisin-black'
                          } h-8 text-sm focus:outline-none transition w-full pl-8 p-1 rounded-[10px] placeholder-battleship-gray`}
                          autoComplete="off"
                          type="text"
                          name="keyword"
                          placeholder="Search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span
                          className={`${
                            themeValue === 'dark'
                              ? 'dark:text-[#787878]'
                              : 'text-[#51526C]'
                          } absolute left-2.5`}
                        >
                          <MagnifyingGlassIcon className="h-4 w-4" />
                        </span>
                      </div>
                      <div className="flex-1 overflow-auto px-5">
                        {favTools
                          .filter((tool) =>
                            tool.title
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((tool) => (
                            <div
                              key={tool._id}
                              className={`flex py-[5px] cursor-pointer select-none items-start gap-2 group text-sm ${
                                themeValue === 'dark'
                                  ? 'text-palladium'
                                  : 'text-agate-violet'
                              }`}
                              onClick={() => onClickTemplate(tool)}
                            >
                              <div className="text-sm flex items-center gap-2 w-full py-[2px] hover:bg-platinum dark:hover:bg-ship-grey rounded">
                                <div className="icon-wrapper">
                                  {getToolIconByCategory(tool)}
                                </div>
                                <div className="flex-1 line-clamp-1">
                                  {tool.title}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    <div className="px-5 text-center">
                      You don't have favorite templates.
                    </div>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </motion.div>
        <Dialog
          open={isDialogOpen}
          onClose={(event, reason) => {
            // Solo se cierra si el motivo NO es "backdropClick"
            if (reason === 'backdropClick') return;
            handleDialogClose();
          }}
          container={document.body} // Aseguramos que se monta en el body
          disableEscapeKeyDown
          BackdropProps={{
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onTouchStart: (e) => e.stopPropagation(),
            onTouchMove: (e) => e.stopPropagation(),
            onPointerDown: (e) => {
              // Detener eventos pointer para evitar que se cierre el power panel
              e.stopPropagation();
              e.preventDefault();
            },
          }}
          PaperProps={{
            onClick: (e) => e.stopPropagation(),
            onTouchStart: (e) => e.stopPropagation(),
            onTouchMove: (e) => e.stopPropagation(),
            onPointerDown: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
            style: {
              borderRadius: '21px',
              backgroundColor: themeValue === 'dark' ? '#252425' : '#F7F7F8',
              width: '90%',
              maxWidth: '400px',
              height: 'auto',
              zIndex: 9999,
            },
            elevation: 0,
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
              fontWeight: 700,
              marginTop: '8px',
              fontFamily: 'Figtree, sans-serif',
              fontSize: '24px',
              lineHeight: '28px',
              '@media (max-width:600px)': {
                fontSize: '20px',
              },
            }}
          >
            Save New LLM Combination
          </DialogTitle>
          <DialogContent
            sx={{
              paddingBottom: '1px',
              '@media (max-width:600px)': {
                padding: '8px',
              },
            }}
          >
            <TextField
              autoFocus
              margin="dense"
              label="Custom Combination Name"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Custom Combination"
              value={comboName}
              onChange={(e) => {
                setComboName(e.target.value);
                setError('');
              }}
              error={Boolean(error)}
              helperText={error}
              InputProps={{
                style: {
                  borderRadius: '12px',
                  padding: 0,
                  backgroundColor:
                    themeValue === 'dark' ? '#323232' : '#FBFBFA',
                },
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              marginTop: '8px',
              marginBottom: '8px',
              gap: '36%',
            }}
          >
            <Button
              onClick={handleDialogClose}
              sx={{
                backgroundColor: themeValue === 'dark' ? '#3B3B45' : '#D4D5E6',
                color: themeValue === 'dark' ? '#7F82C3' : '#5256A6',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor:
                    themeValue === 'dark' ? '#34343E' : '#C7C9DE',
                },
                '@media (max-width:600px)': {
                  fontSize: '14px',
                  padding: '6px 12px',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCombo}
              sx={{
                backgroundColor: '#464BBA',
                color: '#FFFFFF',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#3B40A0',
                },
                '@media (max-width:600px)': {
                  fontSize: '14px',
                  padding: '6px 12px',
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

MobilePowerPanel.displayName = 'MobilePowerPanel';

export default inject('store')(observer(MobilePowerPanel));
